<script setup>
// src/components/User/LoginContextModal.vue
// ─────────────────────────────────────────────────────────────────────────────
// Exibido quando o backend retorna requiresContextSelection: true.
// O usuário escolhe entre operar como titular (owner) ou como membro (managed).
// ─────────────────────────────────────────────────────────────────────────────
import { computed, ref } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import api from "@/services/api";

const emit = defineEmits(["done", "cancel"]);

const userStore = useUserStore();

// Contextos vindos da store (preenchidos pelo login)
const contexts = ref(userStore.pendingContextSelection?.contexts ?? []);
const selected = ref(undefined); // RelationshipId escolhido ou null (owner)
const isLoading = ref(false);
const errorMsg = ref("");
const isLocked = ref(false);

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXTOS SEPARADOS PARA DAR HIERARQUIA VISUAL
// ─────────────────────────────────────────────────────────────────────────────
const ownerContext = computed(() =>
  contexts.value.find((ctx) => ctx.type === "owner") ?? null
);

const managedContexts = computed(() =>
  contexts.value.filter((ctx) => ctx.type === "managed")
);

function contextKey(ctx) {
  if (ctx.type === "owner") return "owner";
  return `managed-${ctx.relationshipId}`;
}

function isContextSelected(ctx) {
  return (
    (ctx.type === "owner" && selected.value === null) ||
    (ctx.type === "managed" && selected.value === ctx.relationshipId)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AÇÕES
// ─────────────────────────────────────────────────────────────────────────────
async function selectContext(ctx) {
  if (isLocked.value) return;

  isLocked.value = true;

  selected.value = ctx.type === "managed" ? ctx.relationshipId : null;
  await confirm();
}

async function confirm() {
  if (selected.value === undefined) return;

  isLoading.value = true;
  errorMsg.value = "";

  try {
    await userStore.confirmLoginContext(selected.value);
    emit("done");
  } catch (err) {
    errorMsg.value =
      err.message || "Erro ao selecionar contexto. Tente novamente.";

    isLocked.value = false;
  } finally {
    isLoading.value = false;
  }
}

function cancel() {
  if (isLocked.value) return;

  isLocked.value = true;

  userStore.pendingContextSelection = null;
  emit("cancel");
}

// ─────────────────────────────────────────────────────────────────────────────
// AVATAR
// ─────────────────────────────────────────────────────────────────────────────
function avatarUrl(ctx) {
  let raw = ctx.photoUrl ?? "";
  if (!raw) return null;

  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

  let base = api.defaults.baseURL || "";
  base = base.replace(/\/api$/, "").replace(/\/+$/, "");

  if (!raw.startsWith("/")) raw = "/" + raw;

  return base + raw;
}

// Iniciais de fallback a partir do label.
// Separadores aceitos: espaço, hífen (-) ou underscore (_).
// Para contexto managed, usa o nome extraído por managedLabelParts
// para evitar que o prefixo "Membro de" contamine as iniciais.
function avatarInitials(ctx) {
  const SEPARATOR = /[\s\-_]+/;

  if (ctx.type === "managed") {
    const name = managedLabelParts(ctx).name ?? "";
    const words = name.trim().split(SEPARATOR).filter(Boolean);

    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }

    return (words[0]?.[0] ?? "?").toUpperCase();
  }

  const words = (ctx.label ?? "").trim().split(SEPARATOR).filter(Boolean);

  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  return (words[0]?.[0] ?? "?").toUpperCase();
}

// Formata "FamilyGuardian" → "Family Guardian"
function relationshipLabel(ctx) {
  if (!ctx.relationshipType) return null;
  return ctx.relationshipType.replace(/([A-Z])/g, " $1").trim();
}

// Divide "Membro de Puellas" em { prefix: "Membro de ", name: "Puellas" }
// Funciona para qualquer label managed que siga o padrão "Membro de <Nome>"
function managedLabelParts(ctx) {
  const match = ctx.label?.match(/^(Membro de\s+)(.+)$/);

  if (match) {
    return {
      prefix: match[1],
      name: match[2],
    };
  }

  return {
    prefix: "",
    name: ctx.label ?? "",
  };
}
</script>

<template>
  <div class="ctx-backdrop" @click.self="cancel">
    <div class="ctx-panel">
      <div class="ctx-header">
        <i class="ctx-header-icon fas fa-user-gear"></i>

        <div>
          <h2 class="ctx-title">Como você quer entrar?</h2>
          <p class="ctx-subtitle">
            Escolha o modo de acesso para esta sessão.
          </p>
        </div>
      </div>

      <div class="ctx-content">
        <!-- ──────────────────────────────────────────────────────────────── -->
        <!-- TITULAR — OPÇÃO PRINCIPAL -->
        <!-- ──────────────────────────────────────────────────────────────── -->
        <button
          v-if="ownerContext"
          type="button"
          class="ctx-owner-card"
          :disabled="isLocked || isLoading"
          :class="{
 'ctx-owner-card--selected': isContextSelected(ownerContext),
          }"
          @click="selectContext(ownerContext)"
        >
          <div class="ctx-owner-avatar-wrapper">
            <div class="ctx-owner-avatar">
              <img
                v-if="avatarUrl(ownerContext)"
                :src="avatarUrl(ownerContext)"
                :alt="ownerContext.label"
                class="ctx-avatar-img"
              />
              <span v-else class="ctx-owner-avatar-initials">
                {{ avatarInitials(ownerContext) }}
              </span>
            </div>
          </div>

          <div class="ctx-owner-body">
            <div class="ctx-owner-topline">
              <span class="ctx-owner-title">Minha conta</span>
            </div>

            <p class="ctx-owner-description">
              Acesso principal como titular.
            </p>
          </div>

          <span v-if="isContextSelected(ownerContext)" class="ctx-owner-check">
            ✓
          </span>
        </button>

        <!-- ──────────────────────────────────────────────────────────────── -->
        <!-- MEMBROS — OPÇÕES SECUNDÁRIAS -->
        <!-- ──────────────────────────────────────────────────────────────── -->
        <div v-if="managedContexts.length" class="ctx-managed-section">
          <div class="ctx-section-header">
            <span class="ctx-section-line"></span>
            <span class="ctx-section-title">Entrar como membro</span>
            <span class="ctx-section-line"></span>
          </div>

          <div class="ctx-managed-list">
            <button
              v-for="ctx in managedContexts"
              :key="contextKey(ctx)"
              type="button"
              class="ctx-managed-card"
              :disabled="isLocked || isLoading"
              :class="{
 'ctx-managed-card--selected': isContextSelected(ctx),
              }"
              @click="selectContext(ctx)"
            >
              <div class="ctx-managed-avatar-wrapper">
                <div class="ctx-managed-avatar">
                  <img
                    v-if="avatarUrl(ctx)"
                    :src="avatarUrl(ctx)"
                    :alt="ctx.label"
                    class="ctx-avatar-img"
                  />
                  <span v-else class="ctx-managed-avatar-initials">
                    {{ avatarInitials(ctx) }}
                  </span>
                </div>
              </div>

              <div class="ctx-managed-body">
                <span class="ctx-managed-label">
                  <span class="ctx-managed-label-prefix">
                    {{ managedLabelParts(ctx).prefix }}
                  </span>
                  <span class="ctx-managed-name">
                    {{ managedLabelParts(ctx).name }}
                  </span>
                </span>

                <span
                  v-if="relationshipLabel(ctx)"
                  class="ctx-managed-rel-badge"
                  title="Tipo de relacionamento com o titular"
                >
                  {{ relationshipLabel(ctx) }}
                </span>
              </div>

              <span v-if="isContextSelected(ctx)" class="ctx-managed-check">
                ✓
              </span>
            </button>
          </div>
        </div>

        <transition name="fade">
          <p v-if="errorMsg" class="ctx-error">{{ errorMsg }}</p>
        </transition>
      </div>

      <div class="ctx-footer">
        <button
          type="button"
          class="btn-ctx-cancel"
          :disabled="isLoading || isLocked"
          @click="cancel"
        >
          Voltar
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ctx-backdrop {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: var(--backdrop);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 9rem 1rem 1rem;
}

.ctx-panel {
  width: 100%;
  max-width: 460px;
  max-height: 90vh;
  background: var(--bg-content);
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* ─────────────────────────────────────────────────────────────────────────────
   HEADER
───────────────────────────────────────────────────────────────────────────── */
.ctx-header {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.1rem 1.5rem;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.ctx-header-icon {
  font-size: 1.5rem;
  line-height: 1;
  color: var(--text-dark);
  flex-shrink: 0;
}

.ctx-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--text-dark);
  margin: 0 0 0.1rem;
}

.ctx-subtitle {
  font-size: 0.8rem;
  color: var(--text-light);
  margin: 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
   CONTENT
───────────────────────────────────────────────────────────────────────────── */
.ctx-content {
  display: flex;
  flex-direction: column;
  padding: 1.2rem 1.5rem 0.25rem;
  min-height: 0;
  flex: 1;
  background: var(--bg-content);
}

/* ─────────────────────────────────────────────────────────────────────────────
   OWNER CARD — DESTAQUE PRINCIPAL
───────────────────────────────────────────────────────────────────────────── */
.ctx-owner-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.95rem;
  width: 100%;
  padding: 1rem;
  border-radius: 15px;
  border: 2px solid #c4b5fd;
  background: linear-gradient(135deg, #f5f3ff 0%, #ffffff 72%);
  box-shadow:
    0 10px 24px rgba(124, 111, 207, 0.13),
    0 0 0 4px rgba(196, 181, 253, 0.14);
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
  flex-shrink: 0;
}

.ctx-owner-card:hover:not(:disabled) {
  border-color: #a78bfa;
  background: linear-gradient(135deg, var(--brand-soft) 0%, #ffffff 76%);
}

.ctx-owner-card:active:not(:disabled) {
  transform: translateY(1px);
}

.ctx-owner-card:focus,
.ctx-owner-card:focus-visible {
  outline: none;
}

.ctx-owner-card--selected {
  border-color: #8b5cf6;
  background: linear-gradient(135deg, var(--brand-soft) 0%, #ffffff 72%);
}

.ctx-owner-card:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.ctx-owner-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.ctx-owner-avatar {
  width: 3.25rem;
  height: 3.25rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--brand-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 5px 14px rgba(15, 23, 42, 0.14);
}

.ctx-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ctx-owner-avatar-initials {
  font-size: 1rem;
  font-weight: 800;
  color: #6d5fc7;
  line-height: 1;
}

.ctx-owner-body {
  flex: 1;
  min-width: 0;
}

.ctx-owner-topline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.ctx-owner-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--text-dark);
}

.ctx-owner-description {
  margin: 0.26rem 0 0;
  font-size: 0.77rem;
  color: var(--text-muted);
  line-height: 1.3;
}

.ctx-owner-check {
  color: #7c3aed;
  font-size: 1.1rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
   MANAGED SECTION
───────────────────────────────────────────────────────────────────────────── */
.ctx-managed-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem;
}

.ctx-section-header {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.65rem;
  flex-shrink: 0;
}

.ctx-section-title {
  color: var(--text-subtle);
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.ctx-section-line {
  height: 1px;
  flex: 1;
  background: var(--neutral-200);
}

.ctx-managed-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  max-height: calc(4 * 3.7rem + 3 * 0.55rem);
  overflow-y: auto;
  padding-right: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: var(--neutral-400) transparent;
}

.ctx-managed-list::-webkit-scrollbar {
  width: 4px;
}

.ctx-managed-list::-webkit-scrollbar-track {
  background: transparent;
}

.ctx-managed-list::-webkit-scrollbar-thumb {
  background: var(--neutral-400);
  border-radius: 999px;
}

.ctx-managed-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 3.7rem;
  padding: 0.62rem 0.85rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background: var(--surface);
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.ctx-managed-card:hover:not(:disabled) {
  border-color: #c4b5fd;
  background: #faf8ff;
}

.ctx-managed-card:active:not(:disabled) {
  transform: translateY(1px);
}

.ctx-managed-card:focus,
.ctx-managed-card:focus-visible {
  outline: none;
}

.ctx-managed-card--selected {
  border-color: #a78bfa;
  background: #f5f3ff;
  box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.16);
}

.ctx-managed-card:disabled {
  opacity: 0.62;
  cursor: not-allowed;
}

.ctx-managed-avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.ctx-managed-avatar {
  width: 2.15rem;
  height: 2.15rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--brand-soft);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ctx-managed-avatar-initials {
  font-size: 0.78rem;
  font-weight: 800;
  color: #7c6fcf;
  line-height: 1;
}

.ctx-managed-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.ctx-managed-label {
  color: var(--text-dark);
  font-size: 0.84rem;
  font-weight: 600;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ctx-managed-label-prefix {
  color: var(--text-subtle);
  font-weight: 500;
}

.ctx-managed-name {
  color: var(--text-dark);
  font-weight: 700;
}

.ctx-managed-rel-badge {
  display: inline-block;
  width: fit-content;
  margin-top: 0.24rem;
  padding: 0.13rem 0.46rem;
  border-radius: 999px;
  background: var(--brand-soft);
  color: #7c6fcf;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ctx-managed-check {
  color: #7c6fcf;
  font-size: 1rem;
  font-weight: 800;
  flex-shrink: 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
   ERROR
───────────────────────────────────────────────────────────────────────────── */
.ctx-error {
  margin: 0.85rem 0 0;
  padding: 0.55rem 0.75rem;
  background: var(--danger-bg);
  color: var(--danger-text);
  border-left: 3px solid var(--danger);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* ─────────────────────────────────────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────────────────────────────────────── */
.ctx-footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem 1.25rem;
  background: var(--bg-content);
  flex-shrink: 0;
}

.btn-ctx-cancel {
  width: 100%;
  padding: 0.58rem 1rem;
  border-radius: 10px;
  border: 1px solid var(--neutral-200);
  background: var(--surface);
  color: var(--text-muted);
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.05s ease;
}

.btn-ctx-cancel:hover:not(:disabled) {
  background: var(--surface-2);
  border-color: var(--neutral-400);
  color: var(--text-dark);
}

.btn-ctx-cancel:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-ctx-cancel:focus,
.btn-ctx-cancel:focus-visible {
  outline: none;
}

.btn-ctx-cancel:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* ─────────────────────────────────────────────────────────────────────────────
   TRANSITIONS
───────────────────────────────────────────────────────────────────────────── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ─────────────────────────────────────────────────────────────────────────────
   RESPONSIVO
───────────────────────────────────────────────────────────────────────────── */
@media (max-width: 520px) {
  .ctx-backdrop {
    padding: 5rem 0.75rem 0.75rem;
  }

  .ctx-panel {
    max-width: 100%;
  }

  .ctx-header {
    padding: 1rem 1.1rem;
  }

  .ctx-content {
    padding: 1rem 1.1rem 0.25rem;
  }

  .ctx-footer {
    padding: 0.75rem 1.1rem 1rem;
  }

  .ctx-owner-card {
    padding: 0.9rem;
  }

  .ctx-owner-avatar {
    width: 3rem;
    height: 3rem;
  }

  .ctx-owner-title {
    font-size: 0.9rem;
  }

  .ctx-owner-description {
    font-size: 0.74rem;
  }
}
</style>